import { CardNumberElement } from '@stripe/react-stripe-js';
import apolloClient from 'apollo/sofarApolloClient';

import google from 'app/lib/google';
import {
  CreatePaymentIntentApiResponse,
  CreatePaymentIntentParameters,
  PromoCodeWithCharges,
} from 'app/shared/typings';
import { AuthContextValues } from 'app/shared/context/Auth';
import { CheckoutStatus, GENERIC_ERROR } from 'app/fan/utils/checkout';
import { CREATE_PAYMENT_INTENT } from 'app/fan/graphql/payments/mutations';
import { createGuestUserAccount } from 'app/fan/graphql/users/mutationHooks';

import completeOrder from './completeOrder';
import confirmPayment from './confirmPayment';

export const createPaymentIntent = (
  variables: CreatePaymentIntentParameters
) => {
  return apolloClient.mutate<
    CreatePaymentIntentApiResponse,
    CreatePaymentIntentParameters
  >({
    mutation: CREATE_PAYMENT_INTENT,
    variables,
  });
};

export const handlePaymentIntentResponse = async ({
  paymentIntentResponse,
  stripe,
  billingRegion,
  eventId,
  numTickets,
  promoCode,
  isVisaFreeTicketRedeemed,
  skipAdditionalAuth = false,
  checkoutVersion = 1,
  isExpressCheckout = false,
  guestUserId,
}: {
  paymentIntentResponse: any;
  stripe: any;
  billingRegion: string;
  eventId: string;
  numTickets: number;
  promoCode: any;
  isVisaFreeTicketRedeemed?: boolean;
  skipAdditionalAuth?: boolean;
  checkoutVersion?: number;
  isExpressCheckout?: boolean;
  guestUserId?: number;
  isGuestCheckout?: boolean;
}) => {
  const {
    data: {
      createPaymentIntent: { requiresAction, paymentIntentId },
    },
  } = paymentIntentResponse;

  if (requiresAction && !skipAdditionalAuth) {
    google.dataLayer({
      event: 'additional_auth',
    });

    const confirmRes = await confirmPayment(
      stripe,
      paymentIntentResponse.data,
      billingRegion
    );

    return completeOrder({
      billingRegion,
      stripePaymentId: confirmRes,
      eventId,
      numTickets,
      promoCode,
      isVisaFreeTicketRedeemed,
      checkoutVersion,
      isExpressCheckout,
      guestUserId,
    });
  } else {
    return completeOrder({
      billingRegion,
      stripePaymentId: paymentIntentId,
      eventId,
      numTickets,
      promoCode,
      isVisaFreeTicketRedeemed,
      checkoutVersion,
      isExpressCheckout,
      guestUserId,
    });
  }
};

/**
 * Main function that's triggered (via a hook) when the user wants to check out on the page.
 *
 * High Level Flow with New Card:
 *  - Garther the payment method + card details using stripe
 *  - Create a payment intent on our api
 *  - Check if the payent method requires a manual confirm (SCA / 3d confirm)
 *    -> requires action:
 *       - call the confirmPayment() method to have the user confirm
 *    -> no action required:
 *       - no further steps here
 *  - Finish the checkout with completeOrder which finishes everything up on our api
 *
 *  High Level Flow with Saved Card:
 *  - Create a payment intent on our api using payment method id from saved card
 *  - Finish the checkout with completeOrder which finishes everything up on our api
 */

interface UseCheckoutProps {
  stripe?: any;
  authContext: AuthContextValues;
  eventId: string;
  numTickets: number;
  storePaymentMethod: boolean;
  finalPrice: number;
  billingRegion: string;
  currency: string;
  promoCode?: PromoCodeWithCharges;
  savedPaymentMethodId?: string;
  isVisaFreeTicketRedeemed?: boolean;
  cardLast4?: string;
  setOutsideError?: (value?: string) => void;
  eventPresale?: boolean;
  elements?: any;
  checkoutVersion?: number;
  isExpressCheckout?: boolean;
  guestCheckoutEnabled?: boolean;
  optInFanMarketingEmails?: boolean;
  guestEmail?: string;
  firstName?: string;
  lastName?: string;
  formikErrors?: any;
}

interface AttemptCheckoutProps extends UseCheckoutProps {
  intl: any;
  setCheckoutStatus: (n: number) => void;
}

export const attemptCheckout = async ({
  intl,
  stripe = undefined,
  authContext,
  eventId,
  numTickets,
  storePaymentMethod,
  finalPrice,
  billingRegion,
  currency,
  setCheckoutStatus,
  promoCode,
  savedPaymentMethodId,
  isVisaFreeTicketRedeemed = false,
  cardLast4,
  setOutsideError,
  eventPresale,
  elements,
  checkoutVersion,
  isExpressCheckout = false,
  guestCheckoutEnabled,
  optInFanMarketingEmails,
  guestEmail,
  firstName,
  lastName,
  formikErrors,
}: AttemptCheckoutProps) => {
  const { loading: sessionLoading, user: loggedInUser, loggedIn } = authContext;

  if (!sessionLoading && !guestCheckoutEnabled && !loggedIn) {
    throw new Error('You are not logged in');
  }

  setCheckoutStatus(CheckoutStatus.LOADING);

  if (finalPrice === 0) {
    if (!loggedIn && !!guestEmail) {
      const { data } = await createGuestUserAccount({
        email: guestEmail,
        firstName,
        lastName,
        optInFanMarketingEmails,
      });

      const createGuestUserAccountData = data?.createGuestUserAccount;

      if (
        createGuestUserAccountData?.success &&
        createGuestUserAccountData?.user
      ) {
        return completeOrder({
          billingRegion,
          eventId,
          numTickets,
          promoCode,
          isVisaFreeTicketRedeemed,
          checkoutVersion,
          isExpressCheckout,
          guestUserId: createGuestUserAccountData.user.id,
        });
      }

      throw new Error(GENERIC_ERROR);
    }

    return completeOrder({
      billingRegion,
      eventId,
      numTickets,
      promoCode,
      isVisaFreeTicketRedeemed,
      checkoutVersion,
      isExpressCheckout,
    });
  }

  let paymentMethodId = savedPaymentMethodId;

  if (!savedPaymentMethodId) {
    const cardNumber = elements.getElement(CardNumberElement);

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber,
      billing_details: {
        email: loggedInUser?.email || guestEmail,
        name: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`,
      },
    });

    if (error) {
      setCheckoutStatus(CheckoutStatus.NONE);
      throw new Error(error.message);
    }

    if (!loggedInUser?.firstName || !loggedInUser?.lastName) {
      if (!firstName || !lastName) {
        throw new Error('First and last name required');
      }
    }

    if (!loggedInUser) {
      if (formikErrors.emailConfirmation) {
        throw new Error('Emails must match');
      }
    }

    const { card } = paymentMethod;

    if (
      card &&
      card.last4 != cardLast4 &&
      (eventPresale || isVisaFreeTicketRedeemed) &&
      setOutsideError
    ) {
      const error = intl.formatMessage({ id: 'checkout.visa.last4Error' });
      setOutsideError(error);
      setCheckoutStatus(CheckoutStatus.NONE);
      throw new Error('Visa Last Four');
    }

    paymentMethodId = paymentMethod.id;
  }

  const amount = Math.round(finalPrice * 100);

  if (!paymentMethodId) {
    throw new Error(GENERIC_ERROR);
  }

  try {
    const paymentIntentResponse = await createPaymentIntent({
      paymentMethodId,
      amount,
      currency,
      billingRegion,
      eventId,
      numTickets,
      storePaymentMethod,
      isGuestCheckout: !loggedIn,
      optinGuestMarketing: optInFanMarketingEmails,
      email: guestEmail,
      firstName,
      lastName,
    });

    if (paymentIntentResponse && paymentIntentResponse.data) {
      const paymentIntentUser =
        paymentIntentResponse.data.createPaymentIntent.user;

      return handlePaymentIntentResponse({
        paymentIntentResponse,
        stripe,
        billingRegion,
        eventId,
        numTickets,
        promoCode,
        isVisaFreeTicketRedeemed,
        isExpressCheckout,
        checkoutVersion,
        guestUserId: !loggedInUser ? paymentIntentUser.id : undefined,
        isGuestCheckout: !loggedIn,
      });
    }

    throw new Error(GENERIC_ERROR);
  } catch (e) {
    setCheckoutStatus(CheckoutStatus.NONE);
    throw new Error((e as any).message || GENERIC_ERROR);
  }
};
